import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { Loader2, Rocket, Save, ExternalLink, Bot, Palette, Smile, Hand, BookOpen, ListChecks, ShieldCheck, MessagesSquare, UserPlus, BarChart3 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { chatApi, BRAND_ID, fmtMoney } from '../components/ChatAssistant/api';
import { Segmented, Modal, btnPrimary, btnGhost } from '../components/ChatAssistant/ui';
import { AppearanceTab, ToneTab, WelcomeTab, BehaviorTab } from '../components/ChatAssistant/ConfigTabs';
import KnowledgeTab from '../components/ChatAssistant/KnowledgeTab';
import { ConversationsTab, LeadsTab, AnalyticsTab } from '../components/ChatAssistant/InboxTabs';
import SecurityTab from '../components/ChatAssistant/SecurityTab';
import LivePreview from '../components/ChatAssistant/LivePreview';

// Site içi AI sohbet asistanı yönetimi. Şimdilik sadece hosgelsin (backend'de de
// marka + e-posta kilidi var). Uçlar: /api/pixel/chat/admin/*
// Kurulum sekmelerinde sağda canlı önizleme durur; sonuç sekmeleri tüm genişliği kullanır.
const SETUP_TABS = [
  { key: 'look', label: 'Görünüm', icon: Palette },
  { key: 'tone', label: 'Kişilik ve ton', icon: Smile },
  { key: 'welcome', label: 'Karşılama', icon: Hand },
  { key: 'knowledge', label: 'Bilgi kaynakları', icon: BookOpen },
  { key: 'rules', label: 'Davranış kuralları', icon: ListChecks },
  { key: 'security', label: 'Model ve bütçe', icon: ShieldCheck },
];
const RESULT_TABS = [
  { key: 'convs', label: 'Konuşmalar', icon: MessagesSquare },
  { key: 'leads', label: "Lead'ler", icon: UserPlus },
  { key: 'analytics', label: 'Analitik', icon: BarChart3 },
];
const isSetup = (k) => SETUP_TABS.some((t) => t.key === k);
const SECTION_LABELS = { appearance: 'Görünüm', tone: 'Kişilik ve ton', welcome: 'Karşılama', behavior: 'Davranış kuralları', consent: 'Onay metinleri', handoff: 'Devir bildirimi' };

const extraOf = (s) => ({
  model: s.model || 'gemini-3.5-flash-lite',
  dailyBudgetUsd: s.dailyBudgetUsd ?? 0,
  retentionDays: s.retentionDays ?? 90,
  catalogSourceUrl: s.catalogSourceUrl || '',
});

export default function ChatAssistantStudio() {
  const { selectedBrand } = useAuth();
  const [settings, setSettings] = useState(null);
  const [config, setConfig] = useState(null);
  const [extra, setExtra] = useState(null);
  const [saved, setSaved] = useState('');
  const [tab, setTab] = useState('look');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);

  const apply = useCallback((s) => {
    setSettings(s);
    setConfig(s.draftConfig);
    const e = extraOf(s);
    setExtra(e);
    setSaved(JSON.stringify({ c: s.draftConfig, e }));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try { apply(await chatApi('/settings')); }
    catch (e) { toast.error(`Yüklenemedi: ${e.message}`); }
    finally { setLoading(false); }
  }, [apply]);
  useEffect(() => { load(); }, [load]);

  const update = useCallback((section, value) => setConfig((c) => ({ ...c, [section]: value })), []);
  const dirty = !!config && JSON.stringify({ c: config, e: extra }) !== saved;

  const save = async () => {
    setSaving(true);
    try {
      const s = await chatApi('/settings', {
        method: 'PUT',
        body: {
          draftConfig: config,
          model: extra.model,
          dailyBudgetUsd: Number(extra.dailyBudgetUsd),
          retentionDays: Number(extra.retentionDays),
          ...(extra.catalogSourceUrl ? { catalogSourceUrl: extra.catalogSourceUrl } : {}),
        },
      });
      apply(s);
      toast.success('Taslak kaydedildi');
      return true;
    } catch (e) { toast.error(`Kaydedilemedi: ${e.message}`); return false; }
    finally { setSaving(false); }
  };

  const changedSections = useMemo(() => {
    if (!config || !settings) return [];
    const pub = settings.publishedConfig || {};
    return Object.keys(config).filter((k) => JSON.stringify(config[k]) !== JSON.stringify(pub[k]));
  }, [config, settings]);

  const publish = async () => {
    if (dirty && !(await save())) return;
    setSaving(true);
    try { apply(await chatApi('/settings/publish', { method: 'POST', body: {} })); toast.success('Yayınlandı'); setPublishOpen(false); }
    catch (e) { toast.error(`Yayınlanamadı: ${e.message}`); }
    finally { setSaving(false); }
  };

  const setVisibility = async (v) => {
    try { setSettings(await chatApi('/settings/visibility', { method: 'POST', body: { visibility: v } })); toast.success(v === 'test' ? 'Test moduna alındı' : 'Kapatıldı'); }
    catch (e) { toast.error(e.message); }
  };

  if (loading || !settings || !config) {
    return <div className="flex items-center justify-center h-[60vh] text-gray-500"><Loader2 className="w-5 h-5 animate-spin mr-2" />Yükleniyor…</div>;
  }
  if (selectedBrand && selectedBrand.id && selectedBrand.id !== BRAND_ID) {
    return <div className="p-8 text-sm text-gray-500">Bu sayfa yalnızca hosgelsin markası için açık.</div>;
  }

  const today = settings.today || {};
  const budget = Number(settings.dailyBudgetUsd || 0);
  const setup = isSetup(tab);

  // İskelet Glowy OnSite (WidgetStudio) ile aynı: üst şerit, solda menü, ortada ayar paneli, sağda gri zeminde önizleme.
  return (
    <div className="flex flex-col h-[calc(100vh-64px)] bg-gray-50 rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3 bg-white border-b border-gray-200">
        <div className="flex items-center gap-3">
          <Bot className="w-5 h-5 text-indigo-600" />
          <div>
            <h1 className="text-[15px] font-semibold text-gray-900">Glowy Desk</h1>
            <p className="text-xs text-gray-500">hosgelsin · sitede ürün öneren, soru yanıtlayan sohbet asistanı</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {dirty
            ? <span className="text-[11px] text-indigo-700">Kaydedilmemiş değişiklik var</span>
            : <span className="text-[11px] text-gray-400">Taslak kayıtlı</span>}
          <a href="https://www.hosgelsin.com/?glowy_chat=1" target="_blank" rel="noreferrer" className={btnGhost} title="Bu tarayıcıda asistanı açar; ?glowy_chat=0 ile kapanır">
            <ExternalLink />Sitede test et
          </a>
          <button className={btnGhost} disabled={!dirty || saving} onClick={save}>{saving ? <Loader2 className="animate-spin" /> : <Save />}Kaydet</button>
          <button className={btnPrimary} disabled={saving} onClick={() => setPublishOpen(true)}><Rocket />Yayınla</button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        <aside className="w-[220px] shrink-0 bg-white border-r border-gray-200 overflow-y-auto flex flex-col">
          <div className="p-3 border-b border-gray-100">
            <h2 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Sitede görünürlük</h2>
            <Segmented
              value={settings.visibility === 'live' ? 'live' : settings.visibility === 'test' ? 'test' : 'off'}
              onChange={setVisibility}
              options={[
                { value: 'off', label: 'Kapalı', dot: 'bg-gray-400' },
                { value: 'test', label: 'Test', dot: 'bg-amber-500' },
                { value: 'live', label: 'Canlı', dot: 'bg-green-500', disabled: true, title: 'Canlıya geçiş Efe onayıyla ayrıca açılacak' },
              ]}
            />
            <p className="mt-2 text-[10px] text-gray-500 leading-snug">
              {settings.visibility === 'test' ? 'Yalnız ?glowy_chat=1 ile gelen tarayıcıda görünür.' : settings.visibility === 'live' ? 'Tüm ziyaretçilerde görünür.' : 'Sitede kimse görmüyor.'}
            </p>
          </div>
          {[['Kurulum', SETUP_TABS], ['Sonuçlar', RESULT_TABS]].map(([group, tabs]) => (
            <nav key={group} className="py-2 border-b border-gray-100">
              <h2 className="px-3 pt-1 pb-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{group}</h2>
              {tabs.map((t) => {
                const Icon = t.icon;
                const active = tab === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTab(t.key)}
                    className={`w-full flex items-center gap-2 px-3 py-2 border-l-2 text-left text-xs ${active ? 'border-indigo-600 bg-indigo-50/60 text-gray-900 font-medium' : 'border-transparent text-gray-600 hover:bg-gray-50'}`}
                  >
                    <Icon className={`w-3.5 h-3.5 shrink-0 ${active ? 'text-indigo-600' : 'text-gray-400'}`} />{t.label}
                  </button>
                );
              })}
            </nav>
          ))}
          <div className="mt-auto p-3 border-t border-gray-100 space-y-1.5 text-[11px] text-gray-500">
            <div className="flex justify-between"><span>Bugünkü harcama</span><span className="tabular-nums text-gray-800 font-medium">{fmtMoney(today.costUsd)}</span></div>
            <div className="flex justify-between"><span>Günlük limit</span><span className="tabular-nums">{budget ? `$${budget.toFixed(2)}` : 'yok'}</span></div>
            {today.previewCostUsd ? <div className="flex justify-between"><span>Önizleme</span><span className="tabular-nums">{fmtMoney(today.previewCostUsd)}</span></div> : null}
            <div className="pt-1.5 border-t border-gray-100 text-[10px] text-gray-400">
              Sürüm {settings.version ?? 0}{settings.publishedAt ? ` · son yayın ${new Date(settings.publishedAt).toLocaleDateString('tr-TR')}` : ' · henüz yayınlanmadı'}
            </div>
          </div>
        </aside>

        {/* Önizleme her zaman bağlı kalır (sohbet sekme değişiminde silinmesin), sonuç sekmelerinde gizlenir */}
        {setup && (
          <aside className="w-[400px] shrink-0 bg-white border-r border-gray-200 overflow-y-auto">
            {tab === 'look' && <AppearanceTab config={config} update={update} />}
            {tab === 'tone' && <ToneTab config={config} update={update} />}
            {tab === 'welcome' && <WelcomeTab config={config} update={update} />}
            {tab === 'knowledge' && <KnowledgeTab />}
            {tab === 'rules' && <BehaviorTab config={config} update={update} />}
            {tab === 'security' && <SecurityTab settings={settings} extra={extra} setExtra={setExtra} />}
          </aside>
        )}
        <main className={`flex-1 min-w-0 p-4 ${setup ? 'overflow-hidden flex' : 'hidden'}`}>
          <LivePreview config={config} models={settings.availableModels || []} settingsModel={extra.model} />
        </main>
        {!setup && (
          <main className={`flex-1 min-w-0 p-4 ${tab === 'convs' ? 'overflow-hidden flex' : 'overflow-y-auto'}`}>
            {tab === 'convs' && <ConversationsTab />}
            {tab === 'leads' && <LeadsTab />}
            {tab === 'analytics' && <AnalyticsTab />}
          </main>
        )}
      </div>

      {publishOpen && (
        <Modal
          title="Yayınla"
          onClose={() => setPublishOpen(false)}
          footer={<><button className={btnGhost} onClick={() => setPublishOpen(false)}>Vazgeç</button><button className={btnPrimary} disabled={saving} onClick={publish}>{saving && <Loader2 className="w-4 h-4 animate-spin" />}Yayınla</button></>}
        >
          {changedSections.length ? (
            <>
              <p className="text-sm text-gray-700 mb-2">Yayındaki sürümden farklı bölümler:</p>
              <ul className="list-disc pl-5 text-sm text-gray-800 mb-3">{changedSections.map((k) => <li key={k}>{SECTION_LABELS[k] || k}</li>)}</ul>
            </>
          ) : <p className="text-sm text-gray-700 mb-3">Taslak yayındakiyle aynı; sürüm numarası yine de artar.</p>}
          {dirty && <p className="text-xs text-amber-700">Kaydedilmemiş değişiklikler önce taslağa kaydedilir.</p>}
          <p className="text-xs text-gray-500 mt-2">Görünürlük şu an: <b>{settings.visibility}</b>. Yayınlanan ayar yalnız görünürlük açıkken (Test: bayraklı ziyaretçi) sitede görünür.</p>
        </Modal>
      )}
    </div>
  );
}
